import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Link } from "react-router-dom";
import { getRenewalReminders, setRenewalReminderStop, runRenewalReminders } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BellRing, RefreshCw, Send } from "lucide-react";

const STEP_LABEL = {
  "d-14": "14 days before", "d-7": "7 days before", "d-3": "3 days before", "d-1": "1 day before",
  d0: "Expiry day", "d+3": "3 days after", "d+7": "7 days after",
};
const CHANNEL = {
  sent: "WhatsApp + email sent", logged: "Logged (test mode)", no_webhook: "No GHL webhook", failed: "GHL failed",
  no_contact: "No phone or email",
};
const fmt = (d) => new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short" });
const daysTo = (end, today) => Math.round((new Date(end + "T00:00:00") - new Date(today + "T00:00:00")) / 86400000);

export default function RenewalReminders() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const isAdmin = atLeast(user?.role, "admin");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getRenewalReminders());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load renewal reminders");
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const toggleStop = async (m) => {
    try {
      await setRenewalReminderStop(m.customer_key, !m.stopped);
      toast.success(m.stopped ? `Reminders back on for ${m.name}` : `Reminders stopped for ${m.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change reminders");
    }
  };

  const runNow = async () => {
    if (!window.confirm("Send today's due reminders now? Members get WhatsApp, email and push straight away.")) return;
    setBusy(true);
    try {
      const r = await runRenewalReminders();
      toast.success(`Sent ${r.sent} reminder${r.sent === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const active = useMemo(() => (data?.members || []).filter((m) => !m.exited), [data]);
  const isManager = atLeast(effectiveRole || user?.role, "manager");
  if (!isManager) return <div className="card-surface p-8 text-sm text-zinc-500">Managers only.</div>;

  return (
    <div className="space-y-6" data-testid="renewal-reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Retention</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Renewal Reminders</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Members at {activeFacility?.name || "this gym"} get a reminder on WhatsApp, email and the app 14, 7, 3 and 1 days
            before their membership ends, on the day, then 3 and 7 days after if they haven&apos;t renewed. It stops on its own
            when a renewal is entered in All Sales or the member is marked exited. 6 Week Challenge clients are left to their coach.
          </p>
        </div>
        <div className="flex gap-2">
          {data?.mode !== "off" && (
            <Button variant="outline" onClick={runNow} disabled={busy} data-testid="renewals-run">
              <Send className="mr-1.5 h-4 w-4" /> Send today&apos;s now
            </Button>
          )}
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {data && (
        <div className="grid gap-3 md:grid-cols-3" data-testid="renewals-status">
          <Status label="Sending" ok={data.mode === "ghl"}
            text={data.mode === "ghl" ? `On, from ${data.reminder_hour}:00 gym time` : data.mode === "console" ? "Test mode (logged, not sent)" : "Off on this server"} />
          <Status label="This gym's GHL workflow (WhatsApp + email)" ok={!!data.webhook} text={data.webhook ? "Connected" : "Not connected"} />
          <Status label="Members in the drip window" ok text={`${active.length} (next 14 days and last 7)`} />
        </div>
      )}

      {data && !data.webhook && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {activeFacility?.name || "This gym"} has no renewal-reminder workflow connected, so only push and in-app reminders go out.{" "}
          {isAdmin ? <Link to="/ghl" className="underline">Connect it on the GoHighLevel page</Link> : "Ask an admin to connect it."}
        </div>
      )}

      <div className="card-surface overflow-x-auto">
        {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : data.members.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            <BellRing className="mx-auto mb-2 h-5 w-5 text-zinc-600" /> No memberships ending in the next 14 days.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="renewals-table">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Ends</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Next</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => {
                const n = daysTo(m.end_date, data.today);
                return (
                  <tr key={m.customer_key} className="border-b border-[#161616] last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-100">{m.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{m.programme}{m.phone ? ` · +${m.phone}` : " · no phone"}{m.email ? "" : " · no email"}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">
                      {fmt(m.end_date)} <span className="text-xs text-zinc-500">({n > 0 ? `in ${n}d` : n === 0 ? "today" : `${-n}d ago`})</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{m.sent.length ? m.sent.map((s) => STEP_LABEL[s]).join(", ") : "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.exited ? <span className="text-zinc-500">Exited</span> : m.stopped ? <span className="text-amber-300">Stopped</span>
                        : m.next_step ? <span className="text-zinc-300">{STEP_LABEL[m.next_step]} · {fmt(m.next_date)}</span>
                          : <span className="text-zinc-500">Done</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!m.exited && (
                        <Button variant="outline" size="sm" onClick={() => toggleStop(m)} data-testid={`renewal-stop-${m.customer_key}`}>
                          {m.stopped ? "Resume" : "Stop"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data?.recent?.length > 0 && (
        <div className="card-surface p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-100">Recently sent</div>
          <ul className="divide-y divide-[#161616] text-xs">
            {data.recent.map((r) => (
              <li key={r.id} className="flex flex-wrap justify-between gap-2 py-2">
                <span className="text-zinc-200">{r.name} · {STEP_LABEL[r.step]}</span>
                <span className="text-zinc-500">
                  {CHANNEL[r.channels?.whatsapp_email] || "Sending…"}{r.channels?.push ? ` · push ${r.channels.push}` : ""} · {new Date(r.sent_at).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Status({ label, ok, text }) {
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm font-semibold ${ok ? "text-zinc-100" : "text-amber-300"}`}>{text}</div>
    </div>
  );
}
